import { useEffect, useMemo, useState } from 'react';
import {
  BLOOD_GROUPS, RH_FACTORS, COMPONENT_CLASSES, COMPONENT_CLASS_LABELS,
  ANTICOAGULANT_LABELS, formatBloodType,
  type BloodGroup, type RhFactor, type ComponentClass,
  type CompatibleDonorOption, type Anticoagulant,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { usePagedList } from '../hooks/usePagedList';
import { DataTable } from '../components/DataTable';

interface ComponentRow {
  id: string; code: string; name: string; class: ComponentClass; classLabel: string;
  storageTempMinC: string | null; storageTempMaxC: string | null;
  requiresAgitation: boolean; isFrozen: boolean; thawedShelfLifeHours: number | null;
  shelfLife: { anticoagulant: Anticoagulant | null; hours: number; days: number; note: string | null }[];
}

/** Grid 4x4 ABO untuk satu kelas komponen. Menampilkan bentuk matriksnya, bukan angka. */
function AboGrid({ cls, matrix }: { cls: ComponentClass; matrix: Map<string, CompatibleDonorOption[]> }) {
  return (
    <div className="card" style={{ padding: '1rem 1.1rem' }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: '0.7rem' }}>
        <h3 style={{ fontSize: '0.95rem' }}>{COMPONENT_CLASS_LABELS[cls]}</h3>
      </div>
      <table style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ width: '5.5rem' }}>Resipien</th>
            {BLOOD_GROUPS.map((g) => <th key={g} style={{ textAlign: 'center' }}>{g}</th>)}
          </tr>
        </thead>
        <tbody>
          {BLOOD_GROUPS.map((r) => {
            const opts = matrix.get(`${cls}|${r}`) ?? [];
            return (
              <tr key={r}>
                <td className="mono" style={{ fontWeight: 500 }}>{r}</td>
                {BLOOD_GROUPS.map((d) => {
                  const hit = opts.find((o) => o.donorAbo === d);
                  const identical = r === d;
                  return (
                    <td key={d} style={{ textAlign: 'center', padding: '0.35rem' }}>
                      {!hit ? (
                        <span className="muted" aria-label="tidak kompatibel">—</span>
                      ) : (
                        <span
                          title={identical ? 'Identik' : 'Substitusi — perlu konfirmasi petugas'}
                          style={{
                            display: 'inline-block', minWidth: '1.6rem',
                            padding: '1px 5px', borderRadius: 5, fontSize: 11,
                            fontFamily: 'JetBrains Mono, monospace',
                            background: identical ? 'var(--accent)' : 'var(--accent-soft)',
                            color: identical ? 'var(--accent-foreground)' : 'var(--accent-strong)',
                            border: identical ? 'none' : '1px solid var(--accent)',
                          }}
                        >
                          {identical ? '✓' : `r${hit.rank}`}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="muted" style={{ fontSize: 12, margin: '0.6rem 0 0' }}>
        Kolom = golongan donor. <span className="mono">✓</span> identik ·{' '}
        <span className="mono">r1/r2</span> substitusi, perlu konfirmasi · — tidak kompatibel
      </p>
    </div>
  );
}

export function BloodReference() {
  const list = usePagedList<ComponentRow>('/blood/components');
  const [matrix, setMatrix] = useState<Map<string, CompatibleDonorOption[]>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Penjelajah pasangan tunggal
  const [cls, setCls] = useState<ComponentClass>('red_cell');
  const [rAbo, setRAbo] = useState<BloodGroup>('O');
  const [rRh, setRRh] = useState<RhFactor>('negative');

  useEffect(() => {
    (async () => {
      try {
        // One row per (class, recipient) to draw the grid. Not paged: the grid IS the
        // whole matrix, and a page of it would be meaningless.
        const entries = await Promise.all(
          COMPONENT_CLASSES.flatMap((c) =>
            BLOOD_GROUPS.map(async (r) => {
              const opts = await api.get<CompatibleDonorOption[]>(
                `/blood/compatible-donors?componentClass=${c}&recipientAbo=${r}&recipientRh=positive`,
              );
              return [`${c}|${r}`, opts] as const;
            }),
          ),
        );
        setMatrix(new Map(entries));
        setError(null);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Gagal memuat data acuan darah.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const options = useMemo(() => matrix.get(`${cls}|${rAbo}`) ?? [], [matrix, cls, rAbo]);
  const rhFiltered = useMemo(
    () => options.filter((o) => cls === 'plasma' || rRh === 'positive' || o.donorRh === 'negative'),
    [options, cls, rRh],
  );

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Acuan Darah</h2>
        <p>
          Matriks kompatibilitas berasal dari seed dan tidak bisa diedit lewat aplikasi —
          perubahannya harus lewat code review.
        </p>
      </div>

      {error && <div className="msg error">{error}</div>}
      {loading && <p className="muted">Memuat…</p>}

      {!loading && (
        <>
          <div className="card" style={{ background: 'var(--accent-soft)', borderColor: 'var(--accent)' }}>
            <h3 style={{ color: 'var(--accent-strong)' }}>Arah matriks sel merah dan plasma TERBALIK</h3>
            <p style={{ margin: '0.4rem 0 0', fontSize: 14 }}>
              Untuk sel merah, antibodi <strong>resipien</strong> menyerang antigen donor — O jadi
              donor universal. Untuk plasma, antibodi <strong>donor</strong> menyerang antigen
              resipien — AB jadi donor universal. Perhatikan bahwa grid plasma di bawah adalah
              transpose dari grid sel merah.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            {COMPONENT_CLASSES.map((c) => <AboGrid key={c} cls={c} matrix={matrix} />)}
          </div>

          <div className="card stack">
            <h3>Cari kandidat donor</h3>
            <div className="grid-2">
              <div className="field">
                <label className="label" htmlFor="cc">Kelas komponen</label>
                <select id="cc" value={cls} onChange={(e) => setCls(e.target.value as ComponentClass)}>
                  {COMPONENT_CLASSES.map((c) => (
                    <option key={c} value={c}>{COMPONENT_CLASS_LABELS[c]}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="label" htmlFor="ra">Golongan resipien</label>
                <select id="ra" value={rAbo} onChange={(e) => setRAbo(e.target.value as BloodGroup)}>
                  {BLOOD_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="field">
                <label className="label" htmlFor="rr">Rhesus resipien</label>
                <select id="rr" value={rRh} onChange={(e) => setRRh(e.target.value as RhFactor)}>
                  {RH_FACTORS.map((f) => (
                    <option key={f} value={f}>{f === 'positive' ? 'Positif' : 'Negatif'}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <p className="label" style={{ marginBottom: '0.5rem' }}>
                Kandidat untuk resipien {formatBloodType(rAbo, rRh)} — {rhFiltered.length} pilihan
              </p>
              <div className="row" style={{ gap: '0.4rem' }}>
                {rhFiltered.length === 0 && <span className="muted">Tidak ada kandidat.</span>}
                {rhFiltered.map((o, i) => (
                  <span
                    key={`${o.donorAbo}-${o.donorRh}-${i}`}
                    className={o.rank === 0 ? 'pill accent' : 'pill'}
                    title={o.requiresConfirmation ? 'Perlu konfirmasi petugas' : 'Identik'}
                  >
                    {formatBloodType(o.donorAbo, o.donorRh)} · r{o.rank}
                    {o.requiresConfirmation ? ' ⚠' : ''}
                  </span>
                ))}
              </div>
              <p className="muted" style={{ fontSize: 12.5, marginTop: '0.6rem' }}>
                Ini pencocokan inventori, <strong>bukan crossmatch</strong>. Crossmatch serologis
                tetap dikerjakan di laboratorium.
              </p>
            </div>
          </div>
        </>
      )}

      {/* No add button: the component list comes from the seed, and changing it has to go
          through code review rather than a form. */}
      <div>
        <h3 style={{ marginBottom: '0.7rem' }}>Komponen &amp; masa simpan</h3>
        <DataTable
          list={list}
          searchPlaceholder="Cari kode atau nama komponen…"
          empty="Belum ada komponen yang diseed."
          emptyFiltered="Tidak ada komponen yang cocok dengan pencarian."
          columns={['Kode', 'Nama', 'Kelas', 'Suhu', 'Masa simpan']}
        >
          {list.items.map((c) => (
            <tr key={c.id}>
              <td className="mono">{c.code}</td>
              <td>
                {c.name}
                {c.requiresAgitation && <span className="pill" style={{ marginLeft: 6 }}>agitasi</span>}
                {c.isFrozen && (
                  <span className="pill accent" style={{ marginLeft: 6 }}>
                    beku · cair {c.thawedShelfLifeHours}j
                  </span>
                )}
              </td>
              <td className="muted">{c.classLabel}</td>
              <td className="mono muted">
                {c.storageTempMinC !== null
                  ? `${Number(c.storageTempMinC)}–${Number(c.storageTempMaxC)} °C`
                  : '—'}
              </td>
              <td style={{ fontSize: 13 }}>
                {c.shelfLife.map((s, i) => (
                  <div key={i}>
                    <span className="mono">
                      {s.hours < 48 ? `${s.hours} jam` : `${s.days} hari`}
                    </span>
                    {s.anticoagulant && (
                      <span className="muted"> · {ANTICOAGULANT_LABELS[s.anticoagulant]}</span>
                    )}
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </DataTable>
      </div>
    </div>
  );
}
